/*
 * Табстопы внутри поддерева. Общая часть трёх ловушек фокуса — Popover, Modal и выезжающей
 * панели навигации AppShell (вынос состоялся на третьем повторении, правило 9).
 *
 * Копии успели разойтись: в Popover не доехала проверка видимости, добавленная в Modal после
 * того, как ловушка выпустила фокус на узел под display: none. Ради этого вынос и делается —
 * дырка в одной копии не видна из соседней.
 *
 * Радиогруппы (у группы табстоп ровно один) остаются надстройкой Modal: только там внутри
 * бывают формы, а лишняя проверка на каждой панели меню — работа впустую.
 */

/**
 * Селектор потенциальных табстопов.
 *
 * `:not(a, b)` не используется намеренно: селектор-лист внутри `:not` даёт другую
 * специфичность, а правило stylelint `selector-not-notation: simple` держит одну запись
 * на весь проект. Поддержка тут ни при чём — Safari 15.4 селектор-лист знает
 * (`docs/ARCHITECTURE.md`, §19).
 */
const FOCUSABLE_SELECTOR = [
  'a[href]',
  'area[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'summary',
  'iframe',
  'audio[controls]',
  'video[controls]',
  '[contenteditable]:not([contenteditable="false"])',
  '[tabindex]:not([tabindex="-1"])',
].join(',')

/**
 * Узлы, по которым внутри `root` ходит Tab, в порядке документа.
 *
 * Отсеиваются три вида узлов, каждый из которых однажды выпустил фокус наружу:
 *
 * — `aria-hidden="true"` на самом узле или на любом его предке внутри `root`: узел есть
 *   в разметке, но не в дереве доступности;
 * — `tabIndex < 0`: роющий фокус (Calendar, Menu, Tabs) держит десятки кнопок с `tabindex="-1"`,
 *   и «последним» узлом панели оказывался недостижимый;
 * — невидимые: свёрнутый блок или второй шаг формы под `display: none` селектору подходит,
 *   но табстопом не является.
 *
 * Видимость проверяется прямоугольниками, а не стилями: `getClientRects()` пуст и у
 * `display: none`, и у пустого инлайна, и стоит один проход раскладки на узел. В среде без
 * раскладки (jsdom) прямоугольников нет ни у кого — там проверка пропускается целиком,
 * иначе ловушка в тестах не нашла бы ни одного узла.
 */
export function focusablesIn(root: HTMLElement): HTMLElement[] {
  const hasLayout = root.getClientRects().length > 0

  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
    (node) =>
      /*
       * closest, а не собственный атрибут: aria-hidden наследуется поддеревом, и кнопка внутри
       * скрытого блока табстопом не является. Проверка на самом узле пропускала её.
       */
      node.closest('[aria-hidden="true"]') === null &&
      /*
       * :disabled ловит и наследованное выключение — контролы внутри fieldset[disabled].
       * Селектор про него не знает (`button:not([disabled])` смотрит только свой атрибут),
       * а focus() по такому узлу роняет фокус в body, то есть за пределы ловушки.
       */
      !node.matches(':disabled') &&
      node.tabIndex >= 0 &&
      (!hasLayout || node.getClientRects().length > 0),
  )
}
